import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePermission } from '../../context/PermissionContext';
import { Search, Filter, RefreshCw, UserPlus, MoreHorizontal, Edit, Trash2, UserCheck, UserX, X, Check } from 'lucide-react';
import { validatePassword } from '../../utils/passwordValidation';
import PasswordRequirements from '../../components/PasswordRequirements';

const AllUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'username', direction: 'ascending' });
  const [page, setPage] = useState(1);
  const [userActionId, setUserActionId] = useState(null);
  
  // Edit user modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
    phoneNumber: '',
    roles: [], // Array of role IDs for role assignment
    language: 'en',
    timezone: 'CST',
    isActive: true,
    forcePasswordReset: false,
    softeonUsername: '',
    lvUsername: '',
    locusUsername: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null); // success, error, or null
  
  // Add user modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addFormData, setAddFormData] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
    phoneNumber: '',
    roles: [], // Array of role IDs for role assignment
    language: 'en',
    timezone: 'CST',
    isActive: true,
    forcePasswordReset: false,
    softeonUsername: '',
    lvUsername: '',
    locusUsername: ''
  });
  const [addFormErrors, setAddFormErrors] = useState({});
  const [addSubmitStatus, setAddSubmitStatus] = useState(null);
  
  // Password validation states
  const [passwordValidation, setPasswordValidation] = useState(null);
  const [addPasswordValidation, setAddPasswordValidation] = useState(null);
  
  const usersPerPage = 10;
  const { currentUser } = useAuth();
  const { hasPermission } = usePermission();
  
  // Get API URL from environment or default
  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';
  
  // Available roles list
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Fetch users from API
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(data);
      setFilteredUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message || 'Failed to load users');
      
      // For development - only use mock data if in development mode
      if (process.env.NODE_ENV === 'development') {
        const mockUsers = [
        ];
        setUsers(mockUsers);
        setFilteredUsers(mockUsers);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch roles from API
  const fetchRoles = async () => {
    setLoadingRoles(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_URL}/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch roles');
      }
      
      const data = await response.json();
      setAvailableRoles(data.roles);
    } catch (err) {
      console.error('Error fetching roles:', err);
      // Fallback to basic roles if API fails
      setAvailableRoles([
        { id: 1, name: 'Administrator' },
        { id: 2, name: 'Manager' },
        { id: 3, name: 'User' },
        { id: 4, name: 'LMS User' }
      ]);
    } finally {
      setLoadingRoles(false);
    }
  };
  
  // Fetch user's roles from the API
  const fetchUserRoles = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_URL}/roles/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch user roles');
      }
      
      const data = await response.json();
      return data.roles;
    } catch (err) {
      console.error(`Error fetching roles for user ${userId}:`, err);
      return [];
    }
  };

  // Fetch users and roles on component mount
  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);
  
  // Apply filters and search whenever filter settings or users change
  useEffect(() => {
    let result = [...users];
    
    // Apply search filter
    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      result = result.filter(user =>
        user.username.toLowerCase().includes(lowerCaseSearchTerm) ||
        user.email.toLowerCase().includes(lowerCaseSearchTerm) ||
        (user.full_name && user.full_name.toLowerCase().includes(lowerCaseSearchTerm))
      );
    }
    
    // Apply role filter
    if (roleFilter !== 'all') {
      result = result.filter(user => user.role === roleFilter);
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      result = result.filter(user => (user.is_active === 1) === isActive);
    }
    
    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    setFilteredUsers(result);
    setPage(1); // Reset to first page when filters change
  }, [users, searchTerm, roleFilter, statusFilter, sortConfig]);
  
  // Request sort for a specific column
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  
  // Get the current page of users
  const currentUsers = filteredUsers.slice(
    (page - 1) * usersPerPage,
    page * usersPerPage
  );
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  // Handle user action menu toggle
  const toggleUserActions = (userId) => {
    setUserActionId(userActionId === userId ? null : userId);
  };
  
  // Handle Add User button click
  const handleAddUserClick = () => {
    // Reset the add form to default values
    setAddFormData({
      username: '',
      email: '',
      password: '',
      fullName: '',
      phoneNumber: '',
      roles: [3], // Default to user role (ID: 3)
      language: 'en',
      timezone: 'CST',
      isActive: true,
      forcePasswordReset: false,
      softeonUsername: '',
      lvUsername: '',
      locusUsername: ''
    });
    setAddFormErrors({});
    setAddSubmitStatus(null);
    setIsAddModalOpen(true);
  };
  
  // Handle edit user button click
  const handleEditUser = (userId) => {
    const userToEdit = users.find(user => user.id === userId);

    if (userToEdit) {
      setEditingUser(userToEdit);

      // Convert role string to roleId for roles array
      let roleId;
      switch(userToEdit.role) {
        case 'admin':
          roleId = 1;
          break;
        case 'manager':
          roleId = 2;
          break;
        case 'lmsuser':
          roleId = 4;
          break;
        case 'user':
        default:
          roleId = 3;
          break;
      }

      setFormData({
        username: userToEdit.username,
        email: userToEdit.email,
        password: '', // Password field starts empty
        fullName: userToEdit.full_name || '',
        phoneNumber: userToEdit.phone_number || '',
        // Only include the roles array for proper role updates
        roles: userToEdit.roles ? userToEdit.roles.map(r => r.id) : [roleId],
        language: userToEdit.language || 'en',
        timezone: userToEdit.timezone || 'CST',
        isActive: userToEdit.is_active === 1,
        forcePasswordReset: userToEdit.force_password_reset === 1,
        softeonUsername: userToEdit.softeon_username || '',
        lvUsername: userToEdit.lv_username || '',
        locusUsername: userToEdit.locus_username || ''
      });
      setFormErrors({});
      setSubmitStatus(null);
      setIsEditModalOpen(true);
    }

    toggleUserActions(null);
  };
  
  // Handle form input changes for Add User form
  const handleAddInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    const newFormData = {
      ...addFormData,
      [name]: type === 'checkbox' ? checked : value
    };
    
    // If changing role, update the roles array to match
    if (name === 'role') {
      // Map role strings to role ID arrays
      let roleId;
      switch (value) {
        case 'admin':
          roleId = 1;
          break;
        case 'manager':
          roleId = 2;
          break;
        case 'lmsuser':
          roleId = 4;
          break;
        case 'user':
        default:
          roleId = 3;
          break;
      }
      newFormData.roles = [roleId];
    }
    
    setAddFormData(newFormData);
    
    // Clear error when field changes
    if (addFormErrors[name]) {
      setAddFormErrors({
        ...addFormErrors,
        [name]: null
      });
    }
    
    // Update password validation when password or role changes
    if (name === 'password' || name === 'role') {
      // Determine role for validation based on roles array
      const currentRole = newFormData.roles && newFormData.roles[0] === 1 ? 'admin' :
                         newFormData.roles && newFormData.roles[0] === 2 ? 'manager' :
                         newFormData.roles && newFormData.roles[0] === 4 ? 'lmsuser' : 'user';
      
      if (newFormData.password) {
        setAddPasswordValidation(validatePassword(newFormData.password, currentRole));
      } else {
        setAddPasswordValidation(null);
      }
    }
  };
  
  // Handle form input changes for Edit User form
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    const newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };

    // If changing role, update the roles array to match
    if (name === 'role') {
      // Set the roles array based on the selected role
      let roleId;
      switch (value) {
        case 'admin':
          roleId = 1;
          break;
        case 'manager':
          roleId = 2;
          break;
        case 'lmsuser':
          roleId = 4;
          break;
        case 'user':
        default:
          roleId = 3;
          break;
      }
      newFormData.roles = [roleId];
    }

    setFormData(newFormData);

    // Clear error when field changes
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }

    // Update password validation when password or role changes
    if (name === 'password' || name === 'role') {
      if (newFormData.password) {
        setPasswordValidation(validatePassword(newFormData.password, newFormData.role));
      } else {
        setPasswordValidation(null);
      }
    }
  };
  
  // Validate form data for both Add and Edit forms
  const validateForm = (data, isAddForm = false) => {
    const errors = {};
    
    // Determine if user is LMS user based on roles array
    const isLmsUser = data.roles && data.roles.includes(4);
    
    // Username validation
    if (!data.username.trim()) {
      errors.username = 'Username is required';
    } else if (data.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }
    
    // Email validation - only required for non-LMS users
    if (!isLmsUser) {
      if (!data.email || !data.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(data.email)) {
        errors.email = 'Please enter a valid email address';
      }
    }
    
    // Password validation - required for new users, optional for edit
    if (isAddForm && !data.password) {
      errors.password = 'Password is required';
    } else if (data.password) {
      // Determine role for validation based on roles array
      const currentRole = data.roles && data.roles[0] === 1 ? 'admin' :
                         data.roles && data.roles[0] === 2 ? 'manager' :
                         data.roles && data.roles[0] === 4 ? 'lmsuser' : 'user';
      
      // Use our shared password validation logic
      const validation = validatePassword(data.password, currentRole);
      if (!validation.isValid) {
        errors.password = validation.message;
      }
    }
    
    // Full name validation - required for all users
    if (!data.fullName || !data.fullName.trim()) {
      errors.fullName = 'Full name is required';
    } else if (data.fullName.length > 100) {
      errors.fullName = 'Full name must be less than 100 characters';
    }
    
    // Language validation - required for LMS users
    if (isLmsUser && !data.language) {
      errors.language = 'Language is required for LMS users';
    }
    
    // Phone validation
    if (data.phoneNumber && data.phoneNumber.length > 20) {
      errors.phoneNumber = 'Phone number must be less than 20 characters';
    }
    
    // External system username validations - all optional but with length constraints
    if (data.softeonUsername && data.softeonUsername.length > 100) {
      errors.softeonUsername = 'Softeon username must be less than 100 characters';
    }
    
    if (data.lvUsername && data.lvUsername.length > 100) {
      errors.lvUsername = 'LV username must be less than 100 characters';
    }
    
    if (data.locusUsername && data.locusUsername.length > 100) {
      errors.locusUsername = 'Locus username must be less than 100 characters';
    }
    
    return errors;
  };
  
  // Handle Add User form submission
  const handleAddUserSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm(addFormData, true);
    if (Object.keys(errors).length > 0) {
      setAddFormErrors(errors);
      return;
    }
    
    // Determine if user is LMS user based on roles array
    const isLmsUser = addFormData.roles && addFormData.roles.includes(4);
    
    // Map roles array to role string for server validation compatibility
    const roleString = addFormData.roles && addFormData.roles[0] === 1 ? 'admin' :
                      addFormData.roles && addFormData.roles[0] === 2 ? 'manager' :
                      addFormData.roles && addFormData.roles[0] === 4 ? 'lmsuser' : 'user';
    
    // Prepare data for API using server-expected field names
    const userData = {
      username: addFormData.username,
      // For LMS users, generate placeholder email; for others, use provided email
      email: isLmsUser ? `${addFormData.username}@lmsuser.local` : addFormData.email,
      password: addFormData.password,
      // Use server's exact field name 'fullName' - server maps this correctly
      fullName: addFormData.fullName || null,
      // Use server's exact field name 'phoneNumber' - server maps this correctly  
      phoneNumber: addFormData.phoneNumber || null,
      // Send BOTH role string (for validation) AND roles array (for controller logic)
      role: roleString,
      roles: addFormData.roles,
      language: addFormData.language || 'en',
      timezone: addFormData.timezone || 'CST',
      // Send boolean values - server will convert as needed
      isActive: addFormData.isActive,
      forcePasswordReset: addFormData.forcePasswordReset,
      softeonUsername: addFormData.softeonUsername || null,
      lvUsername: addFormData.lvUsername || null,
      locusUsername: addFormData.locusUsername || null
    };
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create user');
      }
      
      // Success - update UI
      setAddSubmitStatus('success');
      
      // Refresh user list
      fetchUsers();
      
      // Close modal after delay
      setTimeout(() => {
        setIsAddModalOpen(false);
      }, 1500);
      
    } catch (err) {
      console.error('Error creating user:', err);
      setAddSubmitStatus('error');
      setAddFormErrors({
        ...addFormErrors,
        general: err.message
      });
    }
  };
  
  // Handle Edit User form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    // CRITICAL: Match the EXACT field names expected by the server
    // Prepare data for API using the correct field naming convention from server
    const userData = {
      // Required fields with exact server-side naming
      username: formData.username,
      // Database has NOT NULL constraint - always provide an email address
      email: formData.email,
      // Use server's exact field name 'full_name' instead of 'fullName'
      full_name: formData.fullName || null,
      // Use server's exact field name 'phone_number' instead of 'phoneNumber'
      phone_number: formData.phoneNumber || null,
      // Send only the roles array for proper database updates
      roles: formData.roles,
      language: formData.language || 'en',
      timezone: formData.timezone || 'CST',
      // Server validators expect 'is_active' (note the snake_case) and boolean
      is_active: formData.isActive,
      // Server validators expect 'force_password_reset' (note the snake_case)
      force_password_reset: formData.forcePasswordReset,
      // Integration fields with correct server-side naming
      softeon_username: formData.softeonUsername || null,
      lv_username: formData.lvUsername || null,
      locus_username: formData.locusUsername || null
    };
    
    // Only include password if it was changed
    if (formData.password) {
      userData.password = formData.password;
    }
    
    // Log field mapping for debugging
    console.log('Form data vs. API fields:');
    console.log('username:', formData.username, '→', userData.username);
    console.log('email:', formData.email, '→', userData.email);
    console.log('fullName:', formData.fullName, '→', userData.full_name);
    console.log('phoneNumber:', formData.phoneNumber, '→', userData.phone_number);
    console.log('role:', formData.role, '→', userData.role);
    console.log('isActive:', formData.isActive, '→', userData.is_active);
    
    // Comprehensive debugging: Log detailed request payload with formatting
    console.log('User update request payload (detailed):', JSON.stringify(userData, null, 2));
    console.log('User object being updated:', editingUser);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      // Log all request details before sending
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      console.log('Request headers:', headers);
      console.log('Request URL:', `${API_URL}/users/${editingUser.id}`);
      console.log('Request method:', 'PUT');
      
      // Convert boolean values to match server expectations
      // The server might expect numbers (0/1) instead of booleans
      const serverReadyPayload = {
        ...userData,
        // Convert boolean values to 0/1 integers with correct field names
        is_active: userData.is_active === true ? 1 : 0,
        force_password_reset: userData.force_password_reset === true ? 1 : 0
      };
      
      // Log the modified payload
      console.log('Modified payload for server:', JSON.stringify(serverReadyPayload, null, 2));
      
      // Make the request with the modified payload
      const response = await fetch(`${API_URL}/users/${editingUser.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(serverReadyPayload)
      });
      
      // Try to get the raw response text first
      const rawResponseText = await response.text();
      console.log('Raw response text:', rawResponseText);
      
      // Parse the response as JSON if possible
      let responseData = {};
      try {
        responseData = JSON.parse(rawResponseText);
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', parseError);
        // Continue with empty object
      }
      
      // Detailed logging of the complete response
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries([...response.headers.entries()]));
      console.log('Response body (parsed):', responseData);
      
      if (!response.ok) {
        console.log('Error response detected. Analyzing validation errors...');
        
        // Very detailed error analysis
        if (responseData.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
          console.log('Validation errors found:', responseData.errors);
          
          const fieldErrors = {};
          responseData.errors.forEach(error => {
            console.log(`Field error in '${error.param}': ${error.msg}`);
            fieldErrors[error.param] = error.msg;
          });
          
          setFormErrors({
            ...formErrors,
            ...fieldErrors,
            general: 'Validation failed. Check console for detailed errors.'
          });
        } else if (responseData.message) {
          // Server returned an error message
          console.log('Server error message:', responseData.message);
          setFormErrors({
            ...formErrors,
            general: responseData.message
          });
        } else {
          // Generic error with no details
          console.log('Unknown error with no detailed message');
          throw new Error('Failed to update user - server validation failed');
        }
        
        // Display error on UI
        setSubmitStatus('error');
        return;
      }
      
      // Success - update UI
      setSubmitStatus('success');
      
      // Refresh user list
      fetchUsers();
      
      // Close modal after delay
      setTimeout(() => {
        setIsEditModalOpen(false);
      }, 1500);
      
    } catch (err) {
      console.error('Error updating user:', err);
      setSubmitStatus('error');
      setFormErrors({
        ...formErrors,
        general: err.message
      });
    }
  };
  
  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/users/${userId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to delete user');
        }
        
        // Success - refresh the user list
        fetchUsers();
        
      } catch (err) {
        console.error('Error deleting user:', err);
        alert(`Error: ${err.message}`);
      }
    }
    toggleUserActions(null);
  };
  
  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/users/${userId}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update user status');
      }
      
      // Success - refresh the user list
      fetchUsers();
      
    } catch (err) {
      console.error('Error toggling user status:', err);
      alert(`Error: ${err.message}`);
    }
    toggleUserActions(null);
  };
  
  // Render the Add User modal with two-column layout
  const renderAddUserModal = () => {
    if (!isAddModalOpen) return null;
    
    const isLmsUser = addFormData.roles && addFormData.roles.includes(4);
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-slate-800 rounded-lg shadow-xl w-full max-w-3xl my-8 mx-auto overflow-hidden">
          {/* Modal header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-700">
            <h3 className="text-xl font-semibold text-slate-200">Add New User</h3>
            <button
              onClick={() => setIsAddModalOpen(false)}
              className="text-slate-400 hover:text-slate-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Modal body */}
          <form onSubmit={handleAddUserSubmit} className="p-6">
            {/* Form status messages */}
            {addSubmitStatus === 'success' && (
              <div className="mb-6 p-3 bg-green-900/30 border border-green-800 text-green-300 rounded-md">
                User created successfully!
              </div>
            )}
            
            {addSubmitStatus === 'error' && (
              <div className="mb-6 p-3 bg-red-900/30 border border-red-800 text-red-300 rounded-md">
                {addFormErrors.general || 'Failed to create user. Please try again.'}
              </div>
            )}
            
            {/* Three-column layout container */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Basic Information */}
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-4 pb-2 border-b border-slate-700">Basic Information</h4>
                
                {/* Role dropdown - first field as requested */}
                <div className="mb-4">
                  <label htmlFor="role" className="block text-sm font-medium text-slate-300 mb-1">User Role *</label>
                  <select
                    id="role"
                    name="role"
                    value={addFormData.roles && addFormData.roles[0] === 1 ? 'admin' :
                           addFormData.roles && addFormData.roles[0] === 2 ? 'manager' :
                           addFormData.roles && addFormData.roles[0] === 4 ? 'lmsuser' : 'user'}
                    onChange={handleAddInputChange}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="user">User</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                    <option value="lmsuser">LMS User</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-400">
                    {isLmsUser ? 
                      "LMS users only require username, full name, language, and password" : 
                      "Standard users require username, email, and password"}
                  </p>
                </div>
                
                {/* Username field */}
                <div className="mb-4">
                  <label htmlFor="add-username" className="block text-sm font-medium text-slate-300 mb-1">
                    Username *
                  </label>
                  <input
                    type="text"
                    id="add-username"
                    name="username"
                    value={addFormData.username}
                    onChange={handleAddInputChange}
                    className={`w-full px-3 py-2 bg-slate-700 border ${
                      addFormErrors.username ? 'border-red-500' : 'border-slate-600'
                    } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {addFormErrors.username && (
                    <p className="mt-1 text-sm text-red-500">{addFormErrors.username}</p>
                  )}
                </div>
                
                {/* Email field - not required for LMS users */}
                {!isLmsUser && (
                  <div className="mb-4">
                    <label htmlFor="add-email" className="block text-sm font-medium text-slate-300 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="add-email"
                      name="email"
                      value={addFormData.email}
                      onChange={handleAddInputChange}
                      className={`w-full px-3 py-2 bg-slate-700 border ${
                        addFormErrors.email ? 'border-red-500' : 'border-slate-600'
                      } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {addFormErrors.email && (
                      <p className="mt-1 text-sm text-red-500">{addFormErrors.email}</p>
                    )}
                  </div>
                )}
                
                {/* Password field */}
                <div className="mb-4">
                  <label htmlFor="add-password" className="block text-sm font-medium text-slate-300 mb-1">
                    Password *
                  </label>
                  <input
                    type="password"
                    id="add-password"
                    name="password"
                    value={addFormData.password}
                    onChange={handleAddInputChange}
                    className={`w-full px-3 py-2 bg-slate-700 border ${
                      addFormErrors.password ? 'border-red-500' : (addPasswordValidation?.isValid ? 'border-green-500' : 'border-slate-600')
                    } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {addFormErrors.password && (
                    <p className="mt-1 text-sm text-red-500">{addFormErrors.password}</p>
                  )}
                </div>
                
                {/* Full Name field - required for LMS users */}
                <div className="mb-4">
                  <label htmlFor="add-fullName" className="block text-sm font-medium text-slate-300 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="add-fullName"
                    name="fullName"
                    value={addFormData.fullName}
                    onChange={handleAddInputChange}
                    className={`w-full px-3 py-2 bg-slate-700 border ${
                      addFormErrors.fullName ? 'border-red-500' : 'border-slate-600'
                    } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {addFormErrors.fullName && (
                    <p className="mt-1 text-sm text-red-500">{addFormErrors.fullName}</p>
                  )}
                </div>
                
                {/* Language field - required for LMS users */}
                {isLmsUser && (
                  <div className="mb-4">
                    <label htmlFor="add-language" className="block text-sm font-medium text-slate-300 mb-1">
                      Language *
                    </label>
                    <select
                      id="add-language"
                      name="language"
                      value={addFormData.language}
                      onChange={handleAddInputChange}
                      className={`w-full px-3 py-2 bg-slate-700 border ${
                        addFormErrors.language ? 'border-red-500' : 'border-slate-600'
                      } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="hm">Hmong</option>
                    </select>
                    {addFormErrors.language && (
                      <p className="mt-1 text-sm text-red-500">{addFormErrors.language}</p>
                    )}
                  </div>
                )}
                
              </div>
              
              {/* Middle Column - System Settings */}
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-4 pb-2 border-b border-slate-700">System Settings</h4>
                
                {/* Only show additional fields for non-LMS users */}
                {!isLmsUser && (
                  <>
                    {/* Phone Number field */}
                    <div className="mb-4">
                      <label htmlFor="add-phoneNumber" className="block text-sm font-medium text-slate-300 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="text"
                        id="add-phoneNumber"
                        name="phoneNumber"
                        value={addFormData.phoneNumber}
                        onChange={handleAddInputChange}
                        className={`w-full px-3 py-2 bg-slate-700 border ${
                          addFormErrors.phoneNumber ? 'border-red-500' : 'border-slate-600'
                        } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {addFormErrors.phoneNumber && (
                        <p className="mt-1 text-sm text-red-500">{addFormErrors.phoneNumber}</p>
                      )}
                    </div>
                    
                    {/* Timezone field */}
                    <div className="mb-4">
                      <label htmlFor="add-timezone" className="block text-sm font-medium text-slate-300 mb-1">
                        Timezone
                      </label>
                      <select
                        id="add-timezone"
                        name="timezone"
                        value={addFormData.timezone}
                        onChange={handleAddInputChange}
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="EST">Eastern (EST)</option>
                        <option value="CST">Central (CST)</option>
                        <option value="MST">Mountain (MST)</option>
                        <option value="PST">Pacific (PST)</option>
                      </select>
                    </div>
                  </>
                )}
                
                {/* Account settings section */}
                <div className="space-y-4">
                  {/* Active status toggle */}
                  <div className="mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={addFormData.isActive}
                        onChange={handleAddInputChange}
                        className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                      />
                      <span className="ml-2 text-sm text-slate-300">Active Account</span>
                    </label>
                  </div>
                  
                  {/* Force Password Reset toggle */}
                  <div className="mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="forcePasswordReset"
                        checked={addFormData.forcePasswordReset}
                        onChange={handleAddInputChange}
                        className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                      />
                      <span className="ml-2 text-sm text-slate-300">Force Password Change on First Login</span>
                    </label>
                  </div>
                </div>
                
                {/* Password requirements component - always visible */}
                <div className="mt-6">
                  <h5 className="text-sm font-medium text-slate-300 mb-2">Password Requirements</h5>
                  <PasswordRequirements 
                    role={addFormData.roles && addFormData.roles[0] === 1 ? 'admin' :
                         addFormData.roles && addFormData.roles[0] === 2 ? 'manager' :
                         addFormData.roles && addFormData.roles[0] === 4 ? 'lmsuser' : 'user'} 
                    validationState={addPasswordValidation} 
                  />
                </div>
              </div>
              
              {/* Right Column - External Integrations */}
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-4 pb-2 border-b border-slate-700">External Integrations</h4>
                
                {/* External System Usernames - available for all roles */}
                <div className="space-y-4">
                  {/* Softeon Username */}
                  <div className="mb-4">
                    <label htmlFor="add-softeonUsername" className="block text-sm font-medium text-slate-300 mb-1">
                      Softeon Username
                    </label>
                    <input
                      type="text"
                      id="add-softeonUsername"
                      name="softeonUsername"
                      value={addFormData.softeonUsername}
                      onChange={handleAddInputChange}
                      className={`w-full px-3 py-2 bg-slate-700 border ${
                        addFormErrors.softeonUsername ? 'border-red-500' : 'border-slate-600'
                      } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {addFormErrors.softeonUsername && (
                      <p className="mt-1 text-sm text-red-500">{addFormErrors.softeonUsername}</p>
                    )}
                  </div>
                  
                  {/* LV Username */}
                  <div className="mb-4">
                    <label htmlFor="add-lvUsername" className="block text-sm font-medium text-slate-300 mb-1">
                      LV Username
                    </label>
                    <input
                      type="text"
                      id="add-lvUsername"
                      name="lvUsername"
                      value={addFormData.lvUsername}
                      onChange={handleAddInputChange}
                      className={`w-full px-3 py-2 bg-slate-700 border ${
                        addFormErrors.lvUsername ? 'border-red-500' : 'border-slate-600'
                      } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {addFormErrors.lvUsername && (
                      <p className="mt-1 text-sm text-red-500">{addFormErrors.lvUsername}</p>
                    )}
                  </div>
                  
                  {/* Locus Username */}
                  <div className="mb-4">
                    <label htmlFor="add-locusUsername" className="block text-sm font-medium text-slate-300 mb-1">
                      Locus Username
                    </label>
                    <input
                      type="text"
                      id="add-locusUsername"
                      name="locusUsername"
                      value={addFormData.locusUsername}
                      onChange={handleAddInputChange}
                      className={`w-full px-3 py-2 bg-slate-700 border ${
                        addFormErrors.locusUsername ? 'border-red-500' : 'border-slate-600'
                      } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {addFormErrors.locusUsername && (
                      <p className="mt-1 text-sm text-red-500">{addFormErrors.locusUsername}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Form buttons */}
            <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-slate-700">
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-md transition-colors"
                disabled={addSubmitStatus === 'success'}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                disabled={
                  addSubmitStatus === 'success' || 
                  !addFormData.fullName.trim() ||
                  (addFormData.password && addPasswordValidation && !addPasswordValidation.isValid)
                }
              >
                {addSubmitStatus === 'success' ? 'Created!' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };
  
  // Render the edit user modal with two-column layout
  const renderEditUserModal = () => {
    if (!isEditModalOpen) return null;
    
    const isLmsUser = formData.roles && formData.roles.includes(4);
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-slate-800 rounded-lg shadow-xl w-full max-w-3xl my-8 mx-auto overflow-hidden">
          {/* Modal header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-700">
            <h3 className="text-xl font-semibold text-slate-200">Edit User</h3>
            <button
              onClick={() => setIsEditModalOpen(false)}
              className="text-slate-400 hover:text-slate-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Modal body */}
          <form onSubmit={handleSubmit} className="p-6">
            {/* Form status messages */}
            {submitStatus === 'success' && (
              <div className="mb-6 p-3 bg-green-900/30 border border-green-800 text-green-300 rounded-md">
                User updated successfully!
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mb-6 p-3 bg-red-900/30 border border-red-800 text-red-300 rounded-md">
                {formErrors.general || 'Failed to update user. Please try again.'}
              </div>
            )}
            
            {/* Two-column layout container */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column - Basic Information */}
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-4 pb-2 border-b border-slate-700">Basic Information</h4>
                
                {/* Role selector - first field as requested */}
                <div className="mb-4">
                  <label htmlFor="role" className="block text-sm font-medium text-slate-300 mb-1">User Role</label>
                  <select
                    id="role"
                    name="role"
                    value={formData.roles && formData.roles[0] === 1 ? 'admin' :
                           formData.roles && formData.roles[0] === 2 ? 'manager' :
                           formData.roles && formData.roles[0] === 4 ? 'lmsuser' : 'user'}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!hasPermission('manage_roles')} // Only users with manage_roles permission can change roles
                  >
                    <option value="user">User</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                    <option value="lmsuser">LMS User</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-400">
                    {formData.roles && formData.roles[0] === 4 ?
                      "LMS users only require username, full name, language, and password" :
                      "Standard users require username, email, and password"}
                  </p>
                </div>
                
                {/* Username field */}
                <div className="mb-4">
                  <label htmlFor="username" className="block text-sm font-medium text-slate-300 mb-1">Username</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 bg-slate-700 border ${
                      formErrors.username ? 'border-red-500' : 'border-slate-600'
                    } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {formErrors.username && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.username}</p>
                  )}
                </div>
                
                {/* Email field - not required for LMS users */}
                {!isLmsUser && (
                  <div className="mb-4">
                    <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-1">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 bg-slate-700 border ${
                        formErrors.email ? 'border-red-500' : 'border-slate-600'
                      } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {formErrors.email && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.email}</p>
                    )}
                  </div>
                )}
                
                {/* Password field (optional on edit) */}
                <div className="mb-4">
                  <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-1">
                    Password <span className="text-slate-400">(leave blank to keep unchanged)</span>
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 bg-slate-700 border ${
                      formErrors.password ? 'border-red-500' : (passwordValidation?.isValid ? 'border-green-500' : 'border-slate-600')
                    } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {formErrors.password && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.password}</p>
                  )}
                  
                  {/* Password requirements component */}
                  {formData.password && (
                    <PasswordRequirements 
                      role={formData.roles && formData.roles[0] === 1 ? 'admin' :
                           formData.roles && formData.roles[0] === 2 ? 'manager' :
                           formData.roles && formData.roles[0] === 4 ? 'lmsuser' : 'user'} 
                      validationState={passwordValidation} 
                    />
                  )}
                </div>
                
                {/* Full Name field - required for LMS users */}
                <div className="mb-4">
                  <label htmlFor="fullName" className="block text-sm font-medium text-slate-300 mb-1">
                    Full Name {isLmsUser ? '*' : ''}
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 bg-slate-700 border ${
                      formErrors.fullName ? 'border-red-500' : 'border-slate-600'
                    } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {formErrors.fullName && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.fullName}</p>
                  )}
                </div>
                
                {/* Language field - required for LMS users */}
                {isLmsUser && (
                  <div className="mb-4">
                    <label htmlFor="language" className="block text-sm font-medium text-slate-300 mb-1">
                      Language *
                    </label>
                    <select
                      id="language"
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 bg-slate-700 border ${
                        formErrors.language ? 'border-red-500' : 'border-slate-600'
                      } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="hm">Hmong</option>
                    </select>
                    {formErrors.language && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.language}</p>
                    )}
                  </div>
                )}
                
                {/* Only show these fields for non-LMS users */}
                {!isLmsUser && (
                  <>
                    {/* Phone Number field */}
                    <div className="mb-4">
                      <label htmlFor="phoneNumber" className="block text-sm font-medium text-slate-300 mb-1">Phone Number</label>
                      <input
                        type="text"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 bg-slate-700 border ${
                          formErrors.phoneNumber ? 'border-red-500' : 'border-slate-600'
                        } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      {formErrors.phoneNumber && (
                        <p className="mt-1 text-sm text-red-500">{formErrors.phoneNumber}</p>
                      )}
                    </div>
                    
                    {/* Timezone field */}
                    <div className="mb-4">
                      <label htmlFor="timezone" className="block text-sm font-medium text-slate-300 mb-1">Timezone</label>
                      <select
                        id="timezone"
                        name="timezone"
                        value={formData.timezone}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="EST">Eastern (EST)</option>
                        <option value="CST">Central (CST)</option>
                        <option value="MST">Mountain (MST)</option>
                        <option value="PST">Pacific (PST)</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
              
              {/* Right Column - System Settings and Integrations */}
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-4 pb-2 border-b border-slate-700">System Integrations</h4>
                
                {/* External System Usernames - available for all roles */}
                <div className="mb-6 bg-slate-700/30 p-4 rounded-md border border-slate-600">
                  <h4 className="text-sm font-medium text-slate-300 mb-3">External System Credentials</h4>
                  
                  {/* Softeon Username */}
                  <div className="mb-3">
                    <label htmlFor="softeonUsername" className="block text-sm font-medium text-slate-300 mb-1">
                      Softeon Username
                    </label>
                    <input
                      type="text"
                      id="softeonUsername"
                      name="softeonUsername"
                      value={formData.softeonUsername}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 bg-slate-700 border ${
                        formErrors.softeonUsername ? 'border-red-500' : 'border-slate-600'
                      } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {formErrors.softeonUsername && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.softeonUsername}</p>
                    )}
                  </div>
                  
                  {/* LV Username */}
                  <div className="mb-3">
                    <label htmlFor="lvUsername" className="block text-sm font-medium text-slate-300 mb-1">
                      LV Username
                    </label>
                    <input
                      type="text"
                      id="lvUsername"
                      name="lvUsername"
                      value={formData.lvUsername}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 bg-slate-700 border ${
                        formErrors.lvUsername ? 'border-red-500' : 'border-slate-600'
                      } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {formErrors.lvUsername && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.lvUsername}</p>
                    )}
                  </div>
                  
                  {/* Locus Username */}
                  <div>
                    <label htmlFor="locusUsername" className="block text-sm font-medium text-slate-300 mb-1">
                      Locus Username
                    </label>
                    <input
                      type="text"
                      id="locusUsername"
                      name="locusUsername"
                      value={formData.locusUsername}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 bg-slate-700 border ${
                        formErrors.locusUsername ? 'border-red-500' : 'border-slate-600'
                      } rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {formErrors.locusUsername && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.locusUsername}</p>
                    )}
                  </div>
                </div>
                
                <h4 className="text-sm font-medium text-slate-300 mb-4 pb-2 border-b border-slate-700">Account Settings</h4>
                
                {/* Account settings section */}
                <div className="space-y-4">
                  {/* Active status toggle - only for users with edit_users permission */}
                  {hasPermission('edit_users') && (
                    <div className="mb-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleInputChange}
                          className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                        />
                        <span className="ml-2 text-sm text-slate-300">Active Account</span>
                      </label>
                    </div>
                  )}
                  
                  {/* Force Password Reset toggle */}
                  <div className="mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="forcePasswordReset"
                        checked={formData.forcePasswordReset}
                        onChange={handleInputChange}
                        className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                      />
                      <span className="ml-2 text-sm text-slate-300">Force Password Change on Next Login</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Form buttons */}
            <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-slate-700">
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-md transition-colors"
                disabled={submitStatus === 'success'}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                disabled={
                  submitStatus === 'success' || 
                  (formData.password && passwordValidation && !passwordValidation.isValid)
                }
              >
                {submitStatus === 'success' ? 'Updated!' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };
  
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
      {/* Header with title and actions */}
      <div className="p-6 border-b border-slate-700">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h2 className="text-xl font-semibold text-slate-200">All Users</h2>
          
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search box */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full sm:w-64 bg-slate-700 border border-slate-600 rounded-md text-slate-200 
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                           text-sm placeholder-slate-400"
              />
            </div>
            
            {/* Add New User Button - only show if user has create_users permission */}
            {hasPermission('create_users') && (
              <button 
                onClick={handleAddUserClick}
                className="flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 
                           text-white rounded-md transition-colors whitespace-nowrap text-sm"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add User
              </button>
            )}
          </div>
        </div>
        
        {/* Filters row */}
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center">
            <Filter className="w-4 h-4 text-slate-400 mr-2" />
            <span className="text-sm text-slate-400 mr-2">Filters:</span>
          </div>
          
          {/* Role filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="bg-slate-700 border border-slate-600 rounded-md text-slate-300 px-3 py-1.5 text-sm
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="user">User</option>
            <option value="lmsuser">LMS User</option>
          </select>
          
          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-slate-700 border border-slate-600 rounded-md text-slate-300 px-3 py-1.5 text-sm
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          
          {/* Refresh button */}
          <button 
            onClick={fetchUsers}
            className="ml-auto flex items-center text-sm text-slate-300 hover:text-slate-100"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>
      
      {/* Loading and error states */}
      {loading && (
        <div className="flex justify-center items-center p-8">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-8 w-8 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-slate-300">Loading users...</span>
          </div>
        </div>
      )}
      
      {!loading && error && (
        <div className="flex justify-center items-center p-8">
          <div className="bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-md">
            <p>Error: {error}</p>
            <button
              onClick={fetchUsers}
              className="mt-2 text-sm bg-red-800 hover:bg-red-700 px-3 py-1 rounded-md"
            >
              Try Again
            </button>
          </div>
        </div>
      )}
      
      {/* Users table */}
      {!loading && !error && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/50 text-left">
                <tr>
                  <th className="p-4 text-xs font-medium text-slate-300 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('username')}>
                    <div className="flex items-center">
                      Username
                      {sortConfig.key === 'username' && (
                        <span className="ml-1">{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="p-4 text-xs font-medium text-slate-300 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('email')}>
                    <div className="flex items-center">
                      Email
                      {sortConfig.key === 'email' && (
                        <span className="ml-1">{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="p-4 text-xs font-medium text-slate-300 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('full_name')}>
                    <div className="flex items-center">
                      Name
                      {sortConfig.key === 'full_name' && (
                        <span className="ml-1">{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="p-4 text-xs font-medium text-slate-300 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('role')}>
                    <div className="flex items-center">
                      Role
                      {sortConfig.key === 'role' && (
                        <span className="ml-1">{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="p-4 text-xs font-medium text-slate-300 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('is_active')}>
                    <div className="flex items-center">
                      Status
                      {sortConfig.key === 'is_active' && (
                        <span className="ml-1">{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="p-4 text-xs font-medium text-slate-300 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('last_login')}>
                    <div className="flex items-center">
                      Last Login
                      {sortConfig.key === 'last_login' && (
                        <span className="ml-1">{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="p-4 text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {currentUsers.length > 0 ? (
                  currentUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-slate-700/20">
                      <td className="p-4 text-sm text-slate-300">
                        {user.username}
                        {user.force_password_reset === 1 && (
                          <span className="ml-2 px-1.5 py-0.5 text-xs bg-amber-900/30 text-amber-300 border border-amber-800/50 rounded">
                            Reset
                          </span>
                        )}
                      </td>
                      <td className="p-4 text-sm text-slate-300">
                        {user.email}
                      </td>
                      <td className="p-4 text-sm text-slate-300">
                        {user.full_name || '-'}
                      </td>
                      <td className="p-4">
                        <span className={`
                          inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${user.role === 'admin' ? 'bg-red-900/30 text-red-300 border border-red-800/50' : 
                           user.role === 'manager' ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-800/50' : 
                           user.role === 'lmsuser' ? 'bg-purple-900/30 text-purple-300 border border-purple-800/50' :
                           'bg-blue-900/30 text-blue-300 border border-blue-800/50'}
                        `}>
                          {user.role}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`
                          inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${user.is_active === 1 ? 
                            'bg-green-900/30 text-green-300 border border-green-800/50' : 
                            'bg-gray-900/30 text-gray-300 border border-gray-800/50'}
                        `}>
                          {user.is_active === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-slate-300">
                        {formatDate(user.last_login)}
                      </td>
                      <td className="p-4 text-slate-300 text-center relative">
                        <button 
                          onClick={() => toggleUserActions(user.id)}
                          className="p-1 hover:bg-slate-700 rounded"
                        >
                          <MoreHorizontal className="w-5 h-5" />
                        </button>
                        
                        {/* Actions dropdown */}
                        {userActionId === user.id && (
                          <div className="absolute right-4 mt-2 w-48 bg-slate-800 border border-slate-700 
                                        rounded-md shadow-lg z-10 py-1">
                            <button
                              onClick={() => handleEditUser(user.id)}
                              className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 flex items-center"
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit User
                            </button>
                            
                            <button
                              onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                              className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 flex items-center"
                            >
                              {user.is_active === 1 ? (
                                <>
                                  <UserX className="w-4 h-4 mr-2" />
                                  Deactivate User
                                </>
                              ) : (
                                <>
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  Activate User
                                </>
                              )}
                            </button>
                            
                            {/* Only show delete option if user has delete_users permission */}
                            {hasPermission('delete_users') && (
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-slate-700 flex items-center"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete User
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="p-4 text-center text-slate-400">
                      No users found matching your filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {filteredUsers.length > usersPerPage && (
            <div className="p-4 border-t border-slate-700 flex justify-between items-center">
              <div className="text-sm text-slate-400">
                Showing {(page - 1) * usersPerPage + 1} to {Math.min(page * usersPerPage, filteredUsers.length)} of {filteredUsers.length} users
              </div>
              
              <div className="flex space-x-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                {/* Page numbers - show up to 5 pages */}
                {Array.from(
                  { length: Math.min(5, Math.ceil(filteredUsers.length / usersPerPage)) },
                  (_, i) => {
                    // Adjust the starting page number for larger pagination
                    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
                    let startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
                    if (totalPages <= 5) startPage = 1;
                    const currentPageNum = startPage + i;
                    
                    if (currentPageNum <= totalPages) {
                      return (
                        <button
                          key={currentPageNum}
                          onClick={() => setPage(currentPageNum)}
                          className={`px-3 py-1 rounded ${
                            page === currentPageNum
                              ? 'bg-blue-600 text-white'
                              : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                          }`}
                        >
                          {currentPageNum}
                        </button>
                      );
                    }
                    return null;
                  }
                ).filter(Boolean)}
                
                <button
                  onClick={() => setPage(p => Math.min(Math.ceil(filteredUsers.length / usersPerPage), p + 1))}
                  disabled={page >= Math.ceil(filteredUsers.length / usersPerPage)}
                  className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Render the Add User Modal with two-column layout */}
      {renderAddUserModal()}
      
      {/* Render the Edit User Modal with two-column layout */}
      {renderEditUserModal()}
    </div>
  );
};

export default AllUsers;